import { CompanyOverviewTab_organization$key } from '#app/__generated__/relay/CompanyOverviewTab_organization.graphql.js';
import { Button } from '#app/components/Buttons/Button.js';
import { CompanyPeopleList } from '#app/components/companyProfilesV2/components/CompanyPeopleList/CompanyPeopleList.js';
import {
  AverageCompanyReviewScoreBreakdown,
  BlackStarIcon,
} from '#app/components/companyProfilesV2/components/CompanyRecommendations/AverageCompanyReviewScoreBreakdown.js';
import { CompanyReviewCard } from '#app/components/companyProfilesV2/components/CompanyRecommendations/CompanyReview.js';
import { JobPostingCard } from '#app/components/JobPostingCard/JobPostingCard.js';
import { Link } from '#app/components/Link/index.js';
import { useCarousel } from '#app/hooks/useCarousel.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import { OverviewIndyEmptyState } from './EmptyStates/OverviewIndyEmptyState.js';
import { OverviewJobSectionEmptyState } from './EmptyStates/OverviewJobSectionEmptyState.js';
import { OverviewRecommendationsSectionEmptyState } from './EmptyStates/OverviewRecommendationsSectionEmptyState.js';
import { OverviewTeamSectionEmptyState } from './EmptyStates/OverviewTeamSectionEmptyState.js';
import { ChevronLeftIcon, ChevronRightIcon } from '@contra/icons/icons';
import { graphql, useFragment } from '@contra/relay';
import { getRoute } from '@contra/routes';
import { isTruthy } from '@contra/utilities/isTruthy';
import { Fragment } from 'react';
import { css, styled } from 'styled-components';

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Divider = styled.div`
  ${({ theme }) => css`
    margin: 24px 24px 0 24px;
    background-color: ${theme.colorsV2.gray30};
    height: 1px;
  `}
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 20px;
  border: 1px solid ${({ theme }) => theme.colorsV2.gray30};
`;

const SectionHeader = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 0 24px;
`;

const SectionTitleAndCount = styled.h4`
  ${({ theme }) => css`
    color: ${theme.colorsV2.black};
    font-size: 18px;
    font-weight: 500;
    line-height: 28px;
    letter-spacing: -0.18px;

    display: flex;
    gap: 4px;
    flex-flow: row nowrap;
    align-items: center;
  `}
`;

const RecommendationsContainer = styled.div`
  padding: 24px;
`;

const RecommendationsControls = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
`;

const CarouselDots = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 8px;
`;

const CarouselDot = styled.div<{ $isActive: boolean }>`
  ${({ $isActive, theme }) => css`
    height: 6px;
    width: 6px;
    border-radius: 50%;
    background-color: ${$isActive
      ? theme.colorsV2.gray60
      : theme.colorsV2.gray30};
  `}
`;

const ArrowButton = styled.button`
  ${({ theme }) => css`
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 24x;
    width: 24px;
    transition: color 175ms ease-out;
    color: ${theme.colorsV2.black};

    &:hover {
      color: ${theme.colorsV2.gray60};
    }
  `}
`;

const RecommendationsCarousel = styled.div<{ index: number }>`
  ${({ index }) => css`
    display: flex;
    width: 100%;
    overflow-x: hidden;

    & > * {
      min-width: 100%;
      scroll-snap-type: x mandatory;
      scroll-behavior: smooth;
      scroll-snap-align: start;
      scroll-snap-stop: always;
      scroll-padding: 0 16px;
      scroll-snap-coordinate: 0 0;
      transform: translateX(${index * -100}%);
      transition: transform 0.3s ease-in-out;
      &:not(:last-of-type) {
        border-bottom: none;
      }
    }
  `}
`;

export const CompanyOverviewTab = (props: {
  readonly organizationRef: CompanyOverviewTab_organization$key;
}) => {
  const navigation = useNavigation();
  const data = useFragment(
    graphql`
      fragment CompanyOverviewTab_organization on Organization {
        ...CompanyPeopleList_organization
        name
        slug
        visitorIsMember

        activeJobs: jobs(filter: { statuses: [POSTED] }, first: 2) {
          count
          edges {
            cursor
            node {
              jobPosting {
                id
                ...JobPostingCard_jobPosting
              }
            }
          }
        }

        publicMembers(first: $publicMembersFirst) {
          count
        }

        carouselReviews: userReviews(
          first: 5
          filter: { hideEmptyReviews: true }
        ) {
          count
          edges {
            cursor
            node {
              id
              createdAt
              author {
                id
              }
              reviewText
              ...CompanyReviewCard_userReview
            }
          }
        }
        ...AverageCompanyReviewScoreBreakdown_organization
        ...OverviewJobSectionEmptyState_organization
        ...OverviewRecommendationsSectionEmptyState_organization
        ...OverviewTeamSectionEmptyState_organization
        ...OverviewIndyEmptyState_organization
      }
    `,
    props.organizationRef,
  );

  const splicedReviews = data.carouselReviews.edges.slice(0, 5);
  const { currentImageIndex, onLeftButtonClick, onRightButtonClick } =
    useCarousel({
      imageCount: splicedReviews.length,
    });

  const jobPostingsLength = data.activeJobs.edges.length;
  const userReviewsLength = data.carouselReviews.edges.length;
  const publicMembersLength = data.publicMembers?.count;
  const activeJobs = data.activeJobs.edges
    .map((edge) => edge.node.jobPosting)
    .filter(isTruthy);

  if (!jobPostingsLength && !userReviewsLength && !data.visitorIsMember) {
    return (
      <SectionContainer>
        <OverviewIndyEmptyState organizationRef={data} />
      </SectionContainer>
    );
  }

  return (
    <List>
      {data.activeJobs.edges.length === 0 ? (
        <>
          {data.visitorIsMember ? (
            <SectionContainer>
              <SectionHeader>
                <SectionTitleAndCount>Active Jobs (0)</SectionTitleAndCount>
              </SectionHeader>
              <OverviewJobSectionEmptyState organizationRef={data} />
            </SectionContainer>
          ) : null}
        </>
      ) : (
        <SectionContainer>
          <SectionHeader>
            <SectionTitleAndCount>
              Active Jobs ({data.activeJobs.count})
            </SectionTitleAndCount>
            {data.activeJobs.count > 0 && (
              <Button
                as={Link}
                to={getRoute('/company/@slug/jobs', {
                  args: { slug: data.slug ?? '' },
                })}
                variant="V2Link"
              >
                View all jobs
              </Button>
            )}
          </SectionHeader>
          {activeJobs.map((node, index) => {
            return (
              <Fragment key={node.id}>
                <Divider />
                <JobPostingCard
                  jobPostingRef={node}
                  key={node.id}
                  positionIndex={index + 1}
                  visitorFilterType="ALL"
                />
              </Fragment>
            );
          })}
        </SectionContainer>
      )}

      {userReviewsLength === 0 ? (
        <>
          {data.visitorIsMember ? (
            <SectionContainer>
              <SectionHeader>
                <SectionTitleAndCount>
                  <BlackStarIcon /> Recommendations (0)
                </SectionTitleAndCount>
              </SectionHeader>
              <OverviewRecommendationsSectionEmptyState
                organizationRef={data}
              />
            </SectionContainer>
          ) : null}
        </>
      ) : (
        <SectionContainer>
          <RecommendationsContainer>
            <AverageCompanyReviewScoreBreakdown
              organizationRef={data}
              showViewAll
            />

            <RecommendationsCarousel index={currentImageIndex}>
              {splicedReviews.map((review, index) => (
                <CompanyReviewCard
                  hideOtherReviews
                  hideRecipient={!data.visitorIsMember}
                  key={`${review.node.id}-${index}`}
                  reference={review.node}
                />
              ))}
            </RecommendationsCarousel>
            <RecommendationsControls>
              <ArrowButton
                onClick={onLeftButtonClick}
                type="button"
              >
                <ChevronLeftIcon />
              </ArrowButton>
              <CarouselDots>
                {splicedReviews.map((_, index) => (
                  <CarouselDot
                    $isActive={index === currentImageIndex}
                    key={`review-carousel-dot-${index}`}
                  />
                ))}
              </CarouselDots>
              <ArrowButton
                onClick={onRightButtonClick}
                type="button"
              >
                <ChevronRightIcon />
              </ArrowButton>
            </RecommendationsControls>
          </RecommendationsContainer>
        </SectionContainer>
      )}

      {data.visitorIsMember ? (
        publicMembersLength === 0 ? (
          <SectionContainer>
            <SectionHeader>
              <SectionTitleAndCount>
                {data.name ?? 'Organization'} Team ({publicMembersLength})
              </SectionTitleAndCount>
            </SectionHeader>
            <OverviewTeamSectionEmptyState organizationRef={data} />
          </SectionContainer>
        ) : (
          <CompanyPeopleList
            inviteTeamMembersCTA={false}
            onNavigateToPeopleTab={() => {
              navigation.push(
                getRoute('/company/@slug/people', {
                  args: { slug: data.slug ?? '' },
                }),
              );
            }}
            organizationRef={data}
            showInternalOnlyWarning
          />
        )
      ) : null}
    </List>
  );
};
